import { themeColorsDark } from '@juvare/common-web-ui/JuvareTheme/colors/themeColorsDark';
import { LayoutRows2 } from '@juvare/common-web-ui/LayoutRows2';
import { Box, Button, Dialog, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { Form } from '../Form';
import { Wizard } from '../Wizard';
import { useCommonFormUiTranslation } from '../hooks';
/**
 * A full-screen multi-step wizard component for guided data entry or workflows.
 * It renders a dialog containing step-by-step form or custom content with optional imagery.
 *
 * @component
 *
 * @template RootConfig - The type of root widget configuration, defaulting to `WidgetFormConfig`.
 *
 * @param {FullScreenWizardProps<RootConfig>} props - The properties for configuring the wizard.
 * @param {string} props.title - Title shown at the top of the wizard.
 * @param {FullScreenWizardStep<RootConfig>[]} props.steps - The wizard steps, each containing a form or render function.
 * @param {boolean} props.open - Whether the wizard dialog is open.
 * @param {{ src: string; alt: string; }} [props.image] - Optional image displayed beside form content on large screens.
 * @param {React.ReactNode} [props.imageComponent] - Optional custom image component.
 * @param {string} [props.submitButtonLabel] - Label for the final submit button (defaults to "Submit").
 * @param {string} [props.cancelButtonLabel] - Label for the cancel button (defaults to "Cancel").
 * @param {() => void} props.onCancel - Callback triggered when the cancel button is clicked.
 * @param {any} [props.data] - Initial data passed to wizard steps and forms.
 * @param {(data: any) => void} [props.onChanged] - Callback invoked when form data changes.
 * @param {(data: any) => Promise<any>} [props.onSubmit] - Async callback triggered on form submission.
 * @param {(data: any) => void} [props.onSubmitSuccess] - Callback triggered when form submission succeeds.
 *
 * @returns {React.ReactElement | null} A rendered full-screen wizard dialog or null if there are no steps.
 */
export const FullScreenWizard = (props) => {
    var _a;
    const downMdScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const imageIsUsed = props.image !== undefined || props.imageComponent !== undefined;
    const { t } = useCommonFormUiTranslation();
    const steps = props.steps.map((step) => ({
        label: step.label,
        render: (wizardProps) => {
            let stepContent;
            if (step.render) {
                stepContent = step.render(wizardProps);
            }
            else if (step.form) {
                stepContent = (React.createElement(Form, { config: step.form, key: wizardProps.key, ref: wizardProps.ref, data: wizardProps.data, hideFormControls: true, onChanged: wizardProps.onChanged, onSubmit: wizardProps.onSubmit, onSubmitSuccess: wizardProps.onSubmitSuccess }));
            }
            return (React.createElement(Box, { sx: { display: 'flex', flexGrow: 1, height: '100%' } },
                React.createElement(Box, { sx: {
                        width: !downMdScreen && imageIsUsed ? '50%' : '100%',
                        px: {
                            xs: 2,
                            sm: 4,
                            xl: 5,
                        },
                        py: {
                            xs: 2,
                            sm: 4,
                        },
                        overflow: 'auto',
                    } },
                    step.description && (React.createElement(Typography, { variant: 'd3', sx: { mb: 5 } }, step.description)),
                    stepContent),
                !downMdScreen && props.image && (React.createElement(Box, { sx: {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        width: '50%',
                    } },
                    React.createElement("img", { src: props.image.src, alt: props.image.alt, style: { maxHeight: '100%', maxWidth: '100%' } }))),
                !downMdScreen && props.imageComponent));
        },
    }));
    const id = React.useId();
    const labelId = 'fullscreen-dialog-label-' + id;
    const contentId = 'fullscreen-dialog-content-' + id;
    if (steps && steps.length > 0) {
        return (React.createElement(Dialog, { open: props.open, fullScreen: true, "aria-labelledby": labelId, "aria-describedby": contentId },
            React.createElement(LayoutRows2, { row1: React.createElement(Box, { sx: {
                        alignItems: 'center',
                        // Header is always dark.
                        backgroundColor: themeColorsDark.surface.primary,
                        color: themeColorsDark.text.bold,
                        display: 'flex',
                        justifyContent: 'space-between',
                        px: 5,
                        py: 2,
                    } },
                    React.createElement(Typography, { id: labelId, variant: 'h6' }, props.title),
                    React.createElement(Button, { variant: 'text', color: 'inherit', size: 'large', onClick: props.onCancel, sx: { p: 0 }, "data-test": 'cancel-button' }, (_a = props.cancelButtonLabel) !== null && _a !== void 0 ? _a : t('common.cancel'))), row2: React.createElement(Box, { id: contentId, sx: { height: '100%' } },
                    React.createElement(Wizard, { steps: steps, data: props.data, submitButtonLabel: props.submitButtonLabel, onChanged: props.onChanged, onSubmit: props.onSubmit, onSubmitSuccess: props.onSubmitSuccess, orientation: 'horizontal' })), fullHeightRow: 'row2', row2Scrollable: true })));
    }
    else {
        return null;
    }
};
